import { Theme, CSSObject, List, styled, Divider } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import sizeConfigs from "../../configs/sizeConfigs";
import SlidebarItemWithOutCollapse from "./SlidebarItemWithOutCollapse";
import SidebarItemCollapse from "./SidebarItemCollapse";
import { Link, useNavigate } from "react-router-dom";
import appAdminRoutes from "../../routes/appAdminRoutes";
import useAccessControl from "../hooks/useAccessControl";
import Cookies from "js-cookie";
import appRoutes from "../../routes/appRoutes";
import { useEffect, useState } from "react";
import { useTheme } from "../hooks/useTheme";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

type Props = {
  handleScreen: () => void;
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: sizeConfigs.sidebar.width,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: "0",
  [theme.breakpoints.up("sm")]: {
    width: "0",
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: sizeConfigs.sidebar.width,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));


const AdminSidebar = ({ handleScreen }: Props) => {
  const { appState } = useSelector((state: RootState) => state.appState);
  const { themeStyles } = useTheme();
  const navigate = useNavigate();
  const { permissions }: any = useAccessControl();
  const [showGoBackButton, setShowGoBackButton] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(true);
  const role = Cookies.get("role-jj-rental-ui") || '';
  const isAdmin = role.toLowerCase() === 'admin';

  const findFirstAvailableRoute = () => {
    const hasAnyDashboardChildAccess = () => {
      const pages = [
        "dashboarddailymanifest",
        "dashboardbookings",
        "dashboardavailability",
        "dashboardfleet",
        "dashboardextras",
        "dashboardattempts",
      ];
      const hasAccess = pages.some(page => permissions[page] === true);
      return hasAccess;
    };

    if (role.toLowerCase() !== "admin" && hasAnyDashboardChildAccess()) {
      return "/dashboard";
    }

    for (const route of appRoutes) {
      if (role.toLowerCase() === "admin") {
        if (route.child && route.child.length > 0) {
          return route.child[0].path;
        }
        return route.path;
      }

      const hasAccess = route?.section
        ? permissions[route.section]
        : route.child
          ? route.child.some((child: any) => permissions[child.section] === true)
          : false;

      if (hasAccess) {
        if (route.child && route.child.length > 0) {
          const accessibleChild = route.child.find((child: any) => permissions[child.section] === true);
          if (accessibleChild) {
            return accessibleChild.path;
          }
        }
        return route.path;
      }
    }
    return null;
  };

  useEffect(() => {
    const availableRoute = findFirstAvailableRoute();
    setShowGoBackButton(availableRoute !== null ? true : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, appAdminRoutes]);

  const handleGoBackClick = () => {
    const firstAvailableRoute = findFirstAvailableRoute();
    if (firstAvailableRoute) {
      navigate(firstAvailableRoute);
    }
  };

  return (
    <>
      {
        open && (
          <Drawer
            variant="permanent"
            open={open}
            sx={{
              width: sizeConfigs.sidebar.width,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                backgroundColor: themeStyles.sideBar.bg,
                borderRight: `1px solid ${themeStyles.sideBar.borderColor}`,
              }
            }}
          >
            <List
              disablePadding
              sx={{
                maxHeight: "calc(100vh - 50px)",
                overflowY: "auto",
                overflowX: "hidden"
              }}
            >
              <div className="flex w-full px-[10px] mt-[10px] justify-between items-center">
                <span className="text-[#167AC3] text-[20px] font-semibold">
                  <img src="/images/logo/rd-rental-main-logo.png" alt="logo" width={60} height={60} />
                </span>
                <span
                  className={`material-symbols-outlined text-[30px] ${themeStyles.sideBar.tailwindMenuItemsColor} hover:cursor-pointer`}
                  onClick={() => {
                    setOpen(!open);
                    handleScreen();
                  }}>
                  arrow_left
                </span>
              </div>

              <Divider className={`w-full ${themeStyles.divider.bg}`} />
              <div className="w-full flex pt-[10px] flex-col items-start gap-1.5">
                {appAdminRoutes.map((route, index) => {
                  if (role.toLowerCase() === "admin") {
                    return route.sidebarProps && route.child ? (
                      <SidebarItemCollapse item={route} key={index} />
                    ) : (
                      <SlidebarItemWithOutCollapse item={route} key={index} />
                    );
                  }

                  const hasAccessSection = route?.section
                    ? permissions[route.section]
                    : route.child
                      ? route.child?.filter((child: any) => permissions[child.section] === true).length > 0
                        ? true
                        : false
                      : false;

                  if (role === "admin" || hasAccessSection) {
                    return route.sidebarProps && route.child ? (
                      <SidebarItemCollapse item={route} key={index} />
                    ) : (
                      <SlidebarItemWithOutCollapse item={route} key={index} />
                    );
                  }

                  return null;
                })}
              </div>

            </List>
            <div className={`w-full mt-auto`}>
              {showGoBackButton && (
                <div className={`w-full mt-auto`}>
                  <Divider className={`w-full ${themeStyles.divider.bg}`} />
                  <div className="w-full flex flex-col items-start gap-1.5">
                    <div
                      className={`w-full flex p-[10px] items-center gap-2.5 cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}
                      onClick={handleGoBackClick}
                    >
                      <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[25px]`}>
                        arrow_back
                      </span>

                      <span className={`${themeStyles.sideBar.tailwindMenuItemsColor} text-[13px] font-medium tracking-wide`}>
                        Go Back
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Drawer>
        )
      }


      {!open && (
        <div className={`sidebar close ${themeStyles.sideBar.tailwindBg} h-screen sticky top-0 flex flex-col border-r ${themeStyles.sideBar.tailwindBorderColor}`}>
          <div className={`flex items-center justify-center cursor-pointer mt-[10px] ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}>
            <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[30px]`}
              onClick={() => {
                setOpen(!open);
                handleScreen();
              }}>
              arrow_right
            </span>
          </div>

          <ul className="nav-links max-h-[calc(100vh - 50px)]">
            {appAdminRoutes?.map((item: any, index: number) => {
              return (
                item?.child
                  ?
                  <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}>
                    <div className="iocn-link">
                      <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[20px]`}>
                        {item?.sidebarProps?.icon}
                      </span>
                    </div>

                    <ul className={`sub-menu ${themeStyles.sideBar.tailwindBg} rounded border border-solid border-[#D0D0D0]`}>
                      <li className={`${themeStyles.sideBar.subMenuHeading} font-semibold cursor-default`}>
                        <span className="link_name">
                          {item?.sidebarProps?.displayText}
                        </span>
                      </li>

                      {item?.child?.map((childItem: any, i: number) => {
                        const hasAccessChild = childItem?.section
                          ? (isAdmin || permissions[childItem.section])
                          : false;

                        if (hasAccessChild) {
                          return (
                            <li
                              key={i}
                              className={`${appState === childItem.state ? 'bg-active-rgba text-[#167AC3]' : 'bg-transparent ' + themeStyles.sideBar.tailwindMenuItemsColor} ${themeStyles.sideBar.tailwindMenuItemsHoverColor} font-medium cursor-pointer`}
                            >
                              <Link to={childItem?.path}>
                                {childItem?.sidebarProps?.displayText}
                              </Link>
                            </li>
                          )
                        }

                        return null;
                      })}
                    </ul>
                  </li>
                  :
                  <li
                    key={index}
                    className={`flex items-center justify-center p-[10px] cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}
                  >
                    <span>
                      <Link to={item?.path}>
                        <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[20px]`}>
                          {item?.sidebarProps?.icon}
                        </span>
                      </Link>
                    </span>

                    <ul className={`sub-menu blank ${themeStyles.sideBar.tailwindBg} rounded border border-solid border-[#D0D0D0]`}>
                      <li className={`${appState === item.state ? 'bg-active-rgba text-[#167AC3]' : 'bg-transparent ' + themeStyles.sideBar.tailwindMenuItemsColor} ${themeStyles.sideBar.tailwindMenuItemsHoverColor} font-medium cursor-pointer`}>
                        <Link to={item?.path} className="link_name">
                          {item?.sidebarProps?.displayText}
                        </Link>
                      </li>
                    </ul>
                  </li>
              )
            })}
          </ul>

          {showGoBackButton && (
            <div className={`w-full mt-auto`}>
              <Divider className={`w-full ${themeStyles.divider.bg}`} />

              <div
                className={`flex items-center justify-center p-[10px] cursor-pointer ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`}
                onClick={handleGoBackClick}
              >
                <span className={`material-symbols-outlined ${themeStyles.sideBar.tailwindMenuItemsColor} text-[20px]`}>
                  arrow_back
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
